import CardStatus from './CardStatus';
import SlotHeaderInfo from './SlotHeaderInfo';
import SlotLessonsList from './SlotLessonsList';
import { AppSettings } from '@opanuj/shared/types';
import { Card, Checkbox, Flex, IconButton, Tag, VStack } from '@chakra-ui/react';
import { FiList } from 'react-icons/fi';
import { getCapacityForLessonType, getLessonTypeForCode } from '@opanuj/shared/utils';
import { uniqBy } from 'lodash';
import { useState } from 'react';

function SlotListCard({
	slot,
	settings,
	isActive = true,
	isMultiSelected = false,
	selectedConflictIssue,
	onSelect,
	onToggleSelection,
}: {
	slot: any;
	settings: AppSettings;
	isActive?: boolean;
	isMultiSelected?: boolean;
	selectedConflictIssue?: any;
	onSelect?: () => void;
	onToggleSelection?: () => void;
}) {
	const [showLessons, setShowLessons] = useState<boolean>(false);

	const isReserved: boolean = Boolean(slot?.reservations?.length);
	const isHidden = Boolean(slot?.hidden);
	const hasConflict = Boolean(selectedConflictIssue);
	const isBlocked = Boolean(slot?.isBlocked);
	const reservationCount = slot?.reservations?.length ?? slot?.lessons?.length ?? 0;
	const lessonType = getLessonTypeForCode(slot?.code ?? '');
	const capacity = lessonType ? getCapacityForLessonType(lessonType) : undefined;
	const hasReservations = reservationCount > 0;
	const isStarted = Boolean(slot?.started);
	const isFull = hasReservations && capacity !== undefined && reservationCount >= capacity;
	const isPartiallyFilled = hasReservations && capacity !== undefined && reservationCount < capacity;
	const cardBorderColor = isBlocked ? 'red.300' : hasConflict ? 'red.200' : isActive ? 'blue.400' : 'gray.200';
	const cardBackgroundImage = isHidden
		? 'repeating-linear-gradient(135deg, rgba(0,0,0,0.03), rgba(0,0,0,0.03) 8px, rgba(0,0,0,0.00) 8px, rgba(0,0,0,0.00) 16px)'
		: isStarted && hasReservations
			? 'repeating-linear-gradient(135deg, var(--chakra-colors-blue-50), var(--chakra-colors-blue-50) 8px, var(--chakra-colors-cyan-50) 8px, var(--chakra-colors-cyan-50) 16px)'
		: isPartiallyFilled
			? 'repeating-linear-gradient(135deg, rgba(103, 232, 249, 0.5), rgba(103, 232, 249, 0.5) 8px, rgba(103, 232, 249, 0.0) 8px, rgba(103, 232, 249, 0.0) 16px)'
			: undefined;
	const cardBackground = isBlocked ? 'red.50' : isHidden ? 'gray.100' : isStarted ? 'blue.50' : isFull ? 'cyan.100' : hasReservations ? 'cyan.50' : 'white';

	const renderCheckbox = () => (
		<Checkbox.Root
			checked={isMultiSelected}
			onCheckedChange={(details) => {
				if (details.checked === true || details.checked === false) {
					onToggleSelection?.();
				}
			}}
			onClick={(event) => event.stopPropagation()}
		>
			<Checkbox.HiddenInput />
			<Checkbox.Control />
		</Checkbox.Root>
	);

	if (!isActive) {
		return (
			<Card.Root
				size="sm"
				m={2}
				bg={cardBackground}
				borderWidth="1px"
				minH="48px"
				position="relative"
				overflow="hidden"
				borderColor={cardBorderColor}
				backgroundImage={cardBackgroundImage}
				boxShadow={isActive ? '0 0 0 1px var(--chakra-colors-blue-400)' : 'none'}
				_hover={{ borderColor: 'blue.300' }}
				onClick={onSelect}
				cursor="pointer"
			>
				<Card.Header px={2} py={1}>
					<Flex alignItems="center" justify="center" minH="48px" gap={2}>
						{renderCheckbox()}
						<SlotHeaderInfo slot={slot} />
						<CardStatus slot={slot} selectedConflictIssue={selectedConflictIssue} />
					</Flex>
				</Card.Header>
			</Card.Root>
		);
	}

	return (
		<Card.Root
			bg={cardBackground}
			size="sm"
			m={2}
			minH="48px"
			position="relative"
			overflow="hidden"
			borderWidth="1px"
			borderColor={cardBorderColor}
			backgroundImage={cardBackgroundImage}
			boxShadow={isActive ? '0 0 0 1px var(--chakra-colors-blue-400)' : 'none'}
		>
			<Card.Header px={2} py={1}>
				<Flex alignItems="center" justify="center" minH="48px" gap={2}>
					{renderCheckbox()}
					<SlotHeaderInfo slot={slot} />
					<CardStatus slot={slot} selectedConflictIssue={selectedConflictIssue} />
					{slot.started && (
						<IconButton
							mr={2}
							variant={showLessons ? 'solid' : 'outline'}
							aria-label="list-lessons"
							onClick={() => setShowLessons(!showLessons)}
						>
							<FiList />
						</IconButton>
					)}
				</Flex>
			</Card.Header>
			{showLessons && (
				<Card.Body>
					<SlotLessonsList slot={slot} />
				</Card.Body>
			)}
			{isReserved && (
				<Card.Footer px={3} pb={3} pt={1}>
					<VStack gap={2} align="start">
						{uniqBy(slot?.reservations, 'student.name').map((reservation: any) => {
							const textParts = [`${reservation.student.name} (${reservation.client?.name})`];
							if (reservation.student.grade) {
								textParts.push(`${reservation.student.school}, ${reservation.student.grade}, ${reservation.student.level}`);
							}
							return (
								<Tag.Root
									key={textParts[0]}
									variant="subtle"
									size="lg"
									colorPalette="teal"
								>
									<Tag.Label>{textParts.join(' ')}</Tag.Label>
								</Tag.Root>
							);
						})}
					</VStack>
				</Card.Footer>
			)}
		</Card.Root>
	);
}

export default SlotListCard;
